import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  LinearProgress,
  Divider,
  TextField,
  CircularProgress,
  FormHelperText,
  Collapse,
} from "@mui/material";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SarIcon from "../icons/SarIcon";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import DiamondIcon from "@mui/icons-material/Diamond";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  useInvestorAssetCategoryQuery,
  useGetInvestorStakingPoolsQuery,
  useStakeInPoolMutation,
  useGetMyStakingPositionsQuery,
} from "../../redux/api/authApi";
import StakeConfirmModal from "./StakeConfirmModal";
import PositionDetailsModal from "./PositionDetailsModal";

function formatCurrency(num) {
  if (num == null || Number.isNaN(Number(num))) return "—";
  return `SAR ${Number(num).toLocaleString("en-US")}`;
}

function sanitizeAmount(v) {
  return v.replace(/\D/g, "");
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
}

function positionStatusChipKey(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "active") return "success";
  if (key === "completed" || key === "matured") return "slate";
  if (key === "cancelled" || key === "withdrawn") return "error";
  return "neutral";
}

const cardSx = {
  p: 3,
  borderRadius: 1.5,
  boxShadow: (theme) => theme.palette.shadow.statCard,
  border: (theme) => `1px solid ${theme.palette.border.light}`,
  bgcolor: "background.paper",
};

const StatItem = ({ label, value, valueColor = "text.primary" }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: "text.muted" }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: valueColor }}>
      {value}
    </Typography>
  </Box>
);

const StakingPositionCard = ({
  name,
  staked,
  apy,
  earned,
  unlocksIn,
  lockPeriod,
  progress,
  status = "active",
  onDetailsClick,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 1.25,
      border: (theme) => `1px solid ${theme.palette.border.light}`,
      bgcolor: "background.paper",
      mb: 2,
    }}
  >
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={1.5}
      mb={2}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>
          {name}
        </Typography>
        <Chip
          label={status}
          size="small"
          sx={{
            textTransform: "capitalize",
            bgcolor: (theme) => theme.palette.chips[positionStatusChipKey(status)].bg,
            color: (theme) => theme.palette.chips[positionStatusChipKey(status)].text,
            fontSize: 10,
            fontWeight: 600,
            height: 22,
            borderRadius: 999,
          }}
        />
      </Stack>
      <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
        <Typography sx={{ fontSize: 11, color: "text.muted" }}>APY</Typography>
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 800,
            color: (theme) => theme.palette.chips.success.text,
            letterSpacing: -0.5,
          }}
        >
          {apy}
        </Typography>
      </Box>
    </Stack>

    <Grid container spacing={2} mb={2}>
      <Grid size={{ xs: 6, md: 3 }}>
        <StatItem label="Staked Amount" value={staked} />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <StatItem label="Earned" value={earned} />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <StatItem label="Lock Period" value={lockPeriod} />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <StatItem label="Unlocks In" value={unlocksIn} />
      </Grid>
    </Grid>

    <Box mb={2}>
      <Stack direction="row" justifyContent="space-between" mb={0.75}>
        <Typography sx={{ fontSize: 12, color: "text.muted" }}>
          Lock Period Progress
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.muted", fontWeight: 600 }}>
          {progress}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: "border.light",
          "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            bgcolor: (theme) => theme.palette.primary.main,
          },
        }}
      />
    </Box>

    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
      {/* <Button
        fullWidth
        variant="outlined"
        startIcon={<SarIcon />}
        sx={{
          textTransform: "none",
          borderRadius: 1.25,
          borderColor: "border.light",
          color: "text.primary",
          fontWeight: 600,
          py: 1,
        }}
      >
        Claim Rewards
      </Button> */}
      <Button
        fullWidth
        variant="outlined"
        onClick={onDetailsClick}
        startIcon={<InfoOutlinedIcon />}
        sx={{
          textTransform: "none",
          borderRadius: 1.25,
          borderColor: "border.light",
          color: "common.white",
          background: (theme) => theme.palette.gradients.teal,
          "&:hover": {
            background: (theme) => theme.palette.gradients.tealHover,
          },
          fontWeight: 600,
          py: 1,
        }}
      >
        Details
      </Button>
    </Stack>
  </Paper>
);

const BenefitItem = ({ text }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.75,
      borderRadius: 1.25,
      border: (theme) => `1px solid ${theme.palette.border.light}`,
      bgcolor: "background.paper",
      display: "flex",
      alignItems: "center",
      gap: 1.25,
    }}
  >
    <CheckCircleIcon
      sx={{ fontSize: 20, color: (theme) => theme.palette.chips.success.text }}
    />
    <Typography sx={{ fontSize: 13, color: "text.primary", fontWeight: 500 }}>
      {text}
    </Typography>
  </Paper>
);

const InfoTag = ({ icon, label }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.75}
    sx={{
      px: 1.5,
      py: 0.6,
      borderRadius: 999,
      bgcolor: "background.paper",
      border: (theme) => `1px solid ${theme.palette.border.light}`,
    }}
  >
    {React.cloneElement(icon, {
      sx: { fontSize: 14, color: (theme) => theme.palette.accent.teal.chipText },
    })}
    <Typography sx={{ fontSize: 12, color: "text.primary", fontWeight: 500 }}>
      {label}
    </Typography>
  </Stack>
);

/** Static icon per category name — these 3 categories are fixed and rarely change. */
function getCategoryIconComponent(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("real estate")) return ApartmentOutlinedIcon;
  if (n.includes("commodit")) return DiamondIcon;
  if (n.includes("luxury")) return DiamondOutlinedIcon;
  return CategoryOutlinedIcon;
}

function CategoryGlyph({ name, size = 18 }) {
  const Icon = getCategoryIconComponent(name);
  return <Icon sx={{ fontSize: size }} />;
}

/**
 * Distinct pastel background per category so switching category tabs is
 * immediately recognizable. Reuses existing theme tokens rather than
 * introducing new colors, to stay aligned with the rest of the UI.
 */
function getCategoryCardBg(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("real estate")) return (theme) => theme.palette.accent.cyan.bgLight;
  if (n.includes("commodit")) return (theme) => theme.palette.accent.warning.cardBg;
  if (n.includes("luxury")) return (theme) => theme.palette.chips.violet.bg;
  return (theme) => theme.palette.background.subtle;
}

function validateStakeAmount(value, pool) {
  const minStake = pool?.minStake;
  const amount = parseFloat(value);
  if (!value || !Number.isFinite(amount) || amount <= 0) {
    return "Enter an amount to stake.";
  }
  if (minStake != null && amount < minStake) {
    return `Minimum stake is ${formatCurrency(minStake)}.`;
  }
  return "";
}

/** Per-pool Stake Calculator, rendered beneath a pool card when it's expanded. */
function PoolStakeCalculator({ pool, onStakeNow }) {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const handleAmountChange = (e) => {
    const next = sanitizeAmount(e.target.value);
    setAmount(next);
    if (error) setError("");
  };

  const calc = useMemo(() => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) return null;
    if (pool.minStake != null && amt < pool.minStake) return null;
    const annualYield = Number(pool.annualYield) || 0;
    const lockPeriod = Number(pool.lockPeriod) || 0;
    const reward = amt * (annualYield / 100) * (lockPeriod / 365);
    return { reward, total: amt + reward, lockPeriod };
  }, [amount, pool]);

  const handleStakeNow = () => {
    const err = validateStakeAmount(amount, pool);
    setError(err);
    if (!err) onStakeNow(pool, parseFloat(amount), calc);
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 1.5,
        border: (theme) => `1px solid ${theme.palette.border.light}`,
        bgcolor: "background.paper",
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2, color: "text.primary" }}>
        Stake Calculator
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "text.primary" }}>
        Amount to Stake (SAR)
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={pool.minStake != null ? `Min. ${formatCurrency(pool.minStake)}` : "0"}
        value={amount}
        onChange={handleAmountChange}
        error={Boolean(error)}
        inputProps={{ inputMode: "numeric" }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 1.25,
          },
        }}
      />
      {error ? (
        <FormHelperText error sx={{ mb: 1.5, mt: 0.5 }}>
          {error}
        </FormHelperText>
      ) : (
        <Box sx={{ mb: 2.5 }} />
      )}

      {calc ? (
        <Box
          sx={{
            p: 2.5,
            mb: 2.5,
            borderRadius: 1.5,
            bgcolor: getCategoryCardBg(pool.category),
          }}
        >
          <Grid container spacing={2}>
            <Grid size={6} sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, color: "text.muted" }}>
                Estimated Rewards
              </Typography>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: (theme) => theme.palette.accent.teal.chipText,
                  mt: 0.5,
                }}
              >
                SAR {calc.reward.toFixed(2)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.muted", mt: 0.25 }}>
                after {calc.lockPeriod} days
              </Typography>
            </Grid>
            <Grid size={6} sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, color: "text.muted" }}>
                Total Value
              </Typography>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "text.primary",
                  mt: 0.5,
                }}
              >
                SAR {calc.total.toFixed(2)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.muted", mt: 0.25 }}>
                at maturity
              </Typography>
            </Grid>
          </Grid>
        </Box>
      ) : null}

      <Button
        fullWidth
        variant="contained"
        onClick={handleStakeNow}
        startIcon={<LockOutlinedIcon sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: "none",
          borderRadius: 999,
          py: 1.4,
          fontWeight: 700,
          fontSize: 14,
          color: "common.white",
          background: (theme) => theme.palette.gradients.teal,
          "&:hover": {
            background: (theme) => theme.palette.gradients.tealHover,
          },
        }}
      >
        Stake Now →
      </Button>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1.5, justifyContent: "center" }}>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.muted" }} />
        <Typography sx={{ fontSize: 12, color: "text.muted" }}>
          Staked tokens will be locked for {pool.lockPeriod} days. Early withdrawal may incur penalties.
        </Typography>
      </Stack>
    </Box>
  );
}

const InvestorStaking = () => {
  const [activePoolTab, setActivePoolTab] = useState(null);

  const { data: positionsRes, isLoading: positionsLoading } = useGetMyStakingPositionsQuery();

  const positions = useMemo(() => {
    const rows = Array.isArray(positionsRes?.data) ? positionsRes.data : [];
    return rows.map((row) => {
      const pool = row.poolId && typeof row.poolId === "object" ? row.poolId : {};
      return {
        id: row._id,
        name: pool.poolName || "Staking Pool",
        staked: formatCurrency(row.amountPaid),
        apy: `${pool.annualYield ?? 0}%`,
        lockPeriod: `${pool.lockPeriod ?? "—"} days`,
        earned: formatCurrency(row.rewardAmount),
        unlocksIn: `${row.unlockIn ?? "—"} days`,
        daysRemaining: row.unlockIn ?? "—",
        startDate: formatDate(row.startDate),
        endDate: formatDate(row.endDate),
        progress: Math.min(100, Number(row.lockPeriodProgress) || 0),
        status: row.status,
      };
    });
  }, [positionsRes]);

  const activePositionsCount = positions.filter(
    (p) => String(p.status).toLowerCase() === "active"
  ).length;

  const [detailsPosition, setDetailsPosition] = useState(null);

  const { data: categoriesRes, isLoading: categoriesLoading } = useInvestorAssetCategoryQuery();

  const poolTabs = useMemo(() => {
    const raw = Array.isArray(categoriesRes?.data) ? categoriesRes.data : [];
    return raw
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((c) => ({
        key: c._id,
        label: c.name,
      }));
  }, [categoriesRes]);

  useEffect(() => {
    if (!activePoolTab && poolTabs.length > 0) {
      setActivePoolTab(poolTabs[0].key);
    }
  }, [poolTabs, activePoolTab]);

  const {
    data: poolsRes,
    isLoading: poolsLoading,
    isFetching: poolsFetching,
  } = useGetInvestorStakingPoolsQuery(activePoolTab, { skip: !activePoolTab });

  const pools = useMemo(() => {
    const rows = Array.isArray(poolsRes?.data) ? poolsRes.data : [];
    return rows.map((row) => {
      const asset = row.assetId && typeof row.assetId === "object" ? row.assetId : {};
      return {
        id: row._id,
        poolName: row.poolName,
        assetName: asset.basicInfo?.assetName || "Unnamed asset",
        category: row.category || asset.assetCategory?.name || "",
        annualYield: row.annualYield,
        lockPeriod: row.lockPeriod,
        minStake: row.minStake,
        targetStakeAmount: row.targetStakeAmount,
        totalStaked: row.totalStaked,
        participantsCount: row.participantsCount,
        poolUtilization: row.poolUtilization,
      };
    });
  }, [poolsRes]);

  const [expandedPoolId, setExpandedPoolId] = useState(null);

  useEffect(() => {
    setExpandedPoolId(null);
  }, [activePoolTab]);

  const toggleExpandPool = (poolId) => {
    setExpandedPoolId((prev) => (prev === poolId ? null : poolId));
  };

  const [stakeInPool] = useStakeInPoolMutation();
  const [activeStake, setActiveStake] = useState(null);

  const handleStakeNowFromPool = (pool, amount, calc) => {
    setActiveStake({ pool, amount, calc });
  };

  const handleConfirmStake = async () => {
    await stakeInPool({
      poolId: activeStake.pool.id,
      amountInSAR: activeStake.amount,
    }).unwrap();
    setActiveStake(null);
    setExpandedPoolId(null);
  };

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1.5,
              bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GpsFixedIcon
              sx={{
                color: (theme) => theme.palette.accent.teal.chipText,
                fontSize: 28,
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: "text.primary", letterSpacing: -0.5 }}
            >
              Token Staking
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
              Stake your tokens to earn passive rewards
            </Typography>
          </Box>
        </Stack>

        {/* <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            color: "common.white",
            background: (theme) => theme.palette.gradients.teal,
            borderRadius: 999,
            px: 3,
            py: 1.1,
            "&:hover": {
              background: (theme) => theme.palette.gradients.tealHover,
            },
          }}
        >
          Total Staked: SAR 35,000
        </Button> */}
      </Stack>

      {/* Your staking positions */}
      <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}
            >
              Your Staking Positions
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.muted", mt: 0.25 }}>
              Manage your active stakes
            </Typography>
          </Box>
          <Chip
            label={`${activePositionsCount} Active`}
            size="small"
            sx={{
              bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
              color: (theme) => theme.palette.accent.teal.chipText,
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 999,
            }}
          />
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {positionsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : positions.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "text.muted", textAlign: "center", py: 3 }}>
            You don't have any staking positions yet.
          </Typography>
        ) : (
          positions.map((p) => (
            <StakingPositionCard key={p.id} {...p} onDetailsClick={() => setDetailsPosition(p)} />
          ))
        )}
      </Paper>

      {/* Available Staking Pools */}
      <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
        {/* Section header */}
        <Box mb={2.5}>
          <Typography
            sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}
          >
            Available Staking Pools
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.muted", mt: 0.25 }}>
            Choose a pool and start earning rewards
          </Typography>
        </Box>

        {/* Pool category tab pills */}
        {categoriesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2, mb: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : poolTabs.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "text.muted", mb: 3 }}>
            No asset categories available.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              p: 0.5,
              borderRadius: 999,
              bgcolor: "background.subtle",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              mb: 3,
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {poolTabs.map((t) => {
              const active = activePoolTab === t.key;
              return (
                <Button
                  key={t.key}
                  onClick={() => setActivePoolTab(t.key)}
                  startIcon={<CategoryGlyph name={t.label} />}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    py: 1,
                    borderRadius: 999,
                    minWidth: 0,
                    bgcolor: active ? "background.paper" : "transparent",
                    color: active ? "text.primary" : "text.muted",
                    boxShadow: active
                      ? "0 1px 2px rgba(15,23,42,0.08)"
                      : "none",
                    "&:hover": {
                      bgcolor: active ? "background.paper" : "transparent",
                    },
                  }}
                >
                  {t.label}
                </Button>
              );
            })}
          </Box>
        )}

        {/* Selected pool cards */}
        {poolsLoading || poolsFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4, mb: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : pools.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 1.5,
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              mb: 3,
            }}
          >
            <Typography sx={{ fontSize: 14, color: "text.muted" }}>
              No staking pools available in this category yet.
            </Typography>
          </Box>
        ) : (
          pools.map((pool) => {
            const expanded = expandedPoolId === pool.id;
            return (
            <Box key={pool.id} sx={{ mb: 3 }}>
            <Box
              sx={{
                p: 3,
                borderRadius: 1.5,
                bgcolor: getCategoryCardBg(pool.category),
              }}
            >
              {/* Pool header */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "flex-start" }}
                spacing={2}
                mb={3}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.25,
                      bgcolor: "background.paper",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CategoryGlyph name={pool.category} size={26} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}
                    >
                      {pool.poolName}
                    </Typography>
                    <Box sx={{ mt: 0.75 }}>
                      {pool.assetName ? (
                        <Chip
                          label={`${pool.assetName}`}
                          size="small"
                          sx={{
                            bgcolor: (theme) => theme.palette.primary.main,
                            color: "common.white",
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                            borderRadius: 999,
                          }}
                        />
                      ) : null}
                    </Box>
                  </Box>
                </Stack>

                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                    Annual Yield
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: (theme) => theme.palette.accent.teal.chipText,
                      letterSpacing: -0.5,
                    }}
                  >
                    {pool.annualYield}%
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={3} mb={2}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                    Lock Period
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
                    <LockOutlinedIcon sx={{ fontSize: 14, color: "text.muted" }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>
                      {pool.lockPeriod} days
                    </Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                    Min Stake
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 0.25 }}>
                    {formatCurrency(pool.minStake)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                    Target Stake Amount
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 0.25 }}>
                    {formatCurrency(pool.targetStakeAmount)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                    Total Staked
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 0.25 }}>
                    {formatCurrency(pool.totalStaked)}
                  </Typography>
                </Grid>
              </Grid>

              <Box mb={2}>
                <Stack direction="row" justifyContent="space-between" mb={0.75}>
                  <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                    Pool Utilization
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.muted", fontWeight: 600 }}>
                    {Number(pool.poolUtilization ?? 0).toFixed(1)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Number(pool.poolUtilization) || 0)}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: "background.paper",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      bgcolor: (theme) => theme.palette.primary.main,
                    },
                  }}
                />
              </Box>

              <Stack direction="row" alignItems="center" spacing={0.5} mb={2}>
                <GroupOutlinedIcon sx={{ fontSize: 16, color: "text.muted" }} />
                <Typography sx={{ fontSize: 13, color: "text.primary", fontWeight: 600 }}>
                  {pool.participantsCount ?? 0}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.muted" }}>
                  participants
                </Typography>
              </Stack>

              <Divider sx={{ mb: 1.5, borderColor: (theme) => theme.palette.accent.teal.border }} />

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.5}
                onClick={() => toggleExpandPool(pool.id)}
                sx={{
                  cursor: "pointer",
                  py: 0.75,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "background.paper" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: (theme) => theme.palette.accent.teal.chipText,
                  }}
                >
                  {expanded ? "Hide Calculator" : "Calculate & Stake"}
                </Typography>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    color: (theme) => theme.palette.accent.teal.chipText,
                    transition: "transform 0.2s",
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </Stack>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 2 }}>
                <PoolStakeCalculator pool={pool} onStakeNow={handleStakeNowFromPool} />
              </Box>
            </Collapse>
            </Box>
            );
          })
        )}

        {/* Pool benefits */}
        {/* <Typography
          sx={{ fontSize: 14, fontWeight: 700, mb: 1.5, color: "text.primary" }}
        >
          Pool Benefits
        </Typography>
        <Grid container spacing={2} mb={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <BenefitItem text="Priority access to Marina Heights tokens" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <BenefitItem text="Enhanced voting rights" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <BenefitItem text="Quarterly bonus rewards" />
          </Grid>
        </Grid> */}

      </Paper>

      {/* Bottom info card */}
      {/* <Paper
        elevation={0}
        sx={{
          ...cardSx,
          bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.25,
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AutoAwesomeIcon
              sx={{
                fontSize: 22,
                color: (theme) => theme.palette.accent.teal.chipText,
              }}
            />
          </Box>
          <Box flex={1}>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, mb: 0.5, color: "text.primary" }}
            >
              Maximize Your Returns
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.muted", mb: 2 }}>
              Staking your tokens helps secure the network while earning you
              passive income. All rewards are automatically compounded for
              maximum returns.
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <InfoTag icon={<VerifiedUserOutlinedIcon />} label="VARA Regulated" />
              <InfoTag icon={<GavelOutlinedIcon />} label="Secure Smart Contracts" />
              <InfoTag icon={<PercentIcon />} label="Daily Compounding" />
            </Stack>
          </Box>
        </Stack>
      </Paper> */}

      <StakeConfirmModal
        open={Boolean(activeStake)}
        onClose={() => setActiveStake(null)}
        pool={activeStake?.pool}
        amount={activeStake?.amount ?? 0}
        calc={activeStake?.calc}
        categoryIcon={<CategoryGlyph name={activeStake?.pool?.category} size={26} />}
        onConfirm={handleConfirmStake}
      />

      <PositionDetailsModal
        open={Boolean(detailsPosition)}
        onClose={() => setDetailsPosition(null)}
        position={detailsPosition}
      />
    </Box>
  );
};

export default InvestorStaking;
